import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import { Phone, WhatsappLogo, PencilSimple, Star } from '@phosphor-icons/react';
import AdminNav from '@/components/admin/AdminNav';
import TestimonialModal from '@/components/admin/TestimonialModal';
import { formatTravelMonth } from '@/components/admin/testimonialUtils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const FILTERS = ['All', 'New', 'Contacted', 'Confirmed', 'Completed', 'Cancelled'];
const STATUSES = ['New', 'Contacted', 'Confirmed', 'Completed', 'Cancelled'];

const statusStyle = {
  New: 'bg-[#FFF3E0] text-[#E8651A] border border-[#E8651A]',
  Contacted: 'bg-[#E3F2FD] text-[#1565C0] border border-[#1565C0]/30',
  Confirmed: 'bg-[#E8F5E9] text-[#2E7D32] border border-[#2E7D32]/30',
  Completed: 'bg-[#F3E5F5] text-[#6A1B9A] border border-[#6A1B9A]/30',
  Cancelled: 'bg-[#FFEBEE] text-[#C62828] border border-[#C62828]/30',
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const isToday = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  return (
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear()
  );
};

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [editLead, setEditLead] = useState(null);
  const [editStatus, setEditStatus] = useState('New');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [reviewLead, setReviewLead] = useState(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const prevCountRef = useRef(0);

  const fetchLeads = useCallback(async (showError = false) => {
    try {
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;

      const list = data || [];
      const newCount = list.filter((l) => l.status === 'New').length;
      if (prevCountRef.current > 0 && newCount > prevCountRef.current) {
        const newest = list.find((l) => l.status === 'New');
        if (newest) {
          toast(`🔔 New inquiry from ${newest.phone} · ${newest.from_city} → ${newest.to_city}`, {
            duration: 5000,
            style: {
              background: '#1B2D5E',
              color: 'white',
              borderLeft: '4px solid #E8651A',
            },
          });
        }
      }
      prevCountRef.current = newCount;
      setLeads(list);
    } catch (error) {
      if (showError) {
        toast.error(`Failed to load leads${error.message ? ` — ${error.message}` : ''}`);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeads(true);
    const interval = setInterval(() => fetchLeads(false), 60000);
    return () => clearInterval(interval);
  }, [fetchLeads]);

  const filtered = useMemo(() => {
    let list = leads;
    if (filter !== 'All') {
      list = list.filter((l) => l.status === filter);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (l) =>
          (l.name || '').toLowerCase().includes(q) ||
          (l.phone || '').toLowerCase().includes(q) ||
          (l.from_city || '').toLowerCase().includes(q) ||
          (l.to_city || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [leads, filter, search]);

  const stats = useMemo(
    () => ({
      total: leads.length,
      newToday: leads.filter((l) => l.status === 'New' && isToday(l.created_at)).length,
      contacted: leads.filter((l) => l.status === 'Contacted').length,
      confirmed: leads.filter((l) => l.status === 'Confirmed').length,
    }),
    [leads]
  );

  const openEdit = (lead) => {
    setEditLead(lead);
    setEditStatus(lead.status);
    setEditNotes(lead.notes || '');
  };

  const saveEdit = async () => {
    if (!editLead) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('leads')
        .update({
          status: editStatus,
          notes: editNotes,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editLead.id);
      if (error) throw error;
      toast.success('Lead updated');
      setEditLead(null);
      fetchLeads();
    } catch {
      toast.error('Failed to update lead');
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = () => {
    const headers = ['Date', 'Name', 'Phone', 'From', 'To', 'Travel Date', 'Vehicle', 'Status'];
    const rows = filtered.map((l) => [
      formatDateTime(l.created_at),
      l.name,
      l.phone,
      l.from_city,
      l.to_city,
      l.travel_date,
      l.vehicle,
      l.status,
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'atithi-leads.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const openAddReview = (lead) => {
    setReviewLead(lead);
    setReviewModalOpen(true);
  };

  const reviewInitialValues = useMemo(
    () =>
      reviewLead
        ? {
            customer_name: reviewLead.name !== 'Not provided' ? reviewLead.name : '',
            route: `${reviewLead.from_city} → ${reviewLead.to_city}`,
            travel_month: formatTravelMonth(reviewLead.travel_date),
            source: 'WhatsApp',
          }
        : null,
    [reviewLead],
  );

  const waFollowUp = (lead) => {
    const msg = `Hello ${lead.name}! Following up on your cab inquiry from ${lead.from_city} to ${lead.to_city} on ${lead.travel_date}.`;
    const phone = lead.phone.replace(/\D/g, '');
    return `https://wa.me/91${phone.slice(-10)}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] font-body">
      <AdminNav />

      <div className="max-w-7xl mx-auto px-6 py-10">
        <h1 className="font-display text-3xl text-[#1B2D5E] mb-1">Customer Inquiries</h1>
        <p className="text-[#888] text-sm mb-8">WhatsApp booking requests</p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Leads', value: stats.total },
            { label: 'New Today', value: stats.newToday },
            { label: 'Contacted', value: stats.contacted },
            { label: 'Confirmed', value: stats.confirmed },
          ].map(({ label, value }) => (
            <div
              key={label}
              className="bg-white border border-[#EBEBEB] rounded-xl p-5"
            >
              <div className="font-display text-4xl text-[#1B2D5E] font-bold leading-none">{value}</div>
              <div className="text-xs text-[#888] mt-2">{label}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={`text-[13px] px-4 py-1.5 rounded-md font-medium transition-colors ${
                  filter === f ? 'bg-[#1B2D5E] text-white' : 'text-[#666] hover:bg-[#EFEFEF]'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Input
              placeholder="Search name, phone, city…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-56 h-9 text-sm"
            />
            <Button variant="outline" onClick={exportCsv} className="h-9 text-sm">
              Export CSV
            </Button>
          </div>
        </div>

        <div className="bg-white border border-[#EBEBEB] rounded-xl overflow-hidden">
          {loading ? (
            <p className="p-10 text-center text-[#888]">Loading…</p>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center">
              <div className="text-4xl mb-3">📋</div>
              <p className="font-medium text-[#1B2D5E]">No inquiries yet</p>
              <p className="text-sm text-[#888] mt-1">WhatsApp bookings will appear here</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-[#F0F0F0] text-left text-[#888]">
                    <th className="py-3 px-4 font-medium">#</th>
                    <th className="py-3 px-4 font-medium">Date &amp; Time</th>
                    <th className="py-3 px-4 font-medium">Name</th>
                    <th className="py-3 px-4 font-medium">Phone</th>
                    <th className="py-3 px-4 font-medium">From → To</th>
                    <th className="py-3 px-4 font-medium">Date</th>
                    <th className="py-3 px-4 font-medium">Vehicle</th>
                    <th className="py-3 px-4 font-medium">Status</th>
                    <th className="py-3 px-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((lead, idx) => (
                    <tr
                      key={lead.id}
                      className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA] transition-colors"
                    >
                      <td className="py-3.5 px-4 text-[#888]">{idx + 1}</td>
                      <td className="py-3.5 px-4 whitespace-nowrap">{formatDateTime(lead.created_at)}</td>
                      <td className="py-3.5 px-4">{lead.name}</td>
                      <td className="py-3.5 px-4 whitespace-nowrap">{lead.phone}</td>
                      <td className="py-3.5 px-4">
                        {lead.from_city} → {lead.to_city}
                      </td>
                      <td className="py-3.5 px-4 whitespace-nowrap">{lead.travel_date}</td>
                      <td className="py-3.5 px-4 max-w-[140px] truncate" title={lead.vehicle}>
                        {lead.vehicle}
                      </td>
                      <td className="py-3.5 px-4">
                        <span
                          className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
                            statusStyle[lead.status] || 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {lead.status}
                        </span>
                      </td>
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-1.5">
                          <a
                            href={`tel:${lead.phone}`}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded bg-[#F0F0F0] hover:bg-[#E8651A] hover:text-white transition-colors"
                            title="Call"
                          >
                            <Phone size={14} />
                            Call
                          </a>
                          <a
                            href={waFollowUp(lead)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded bg-[#25D366] text-white hover:bg-[#1EA855] transition-colors"
                            title="WhatsApp"
                          >
                            <WhatsappLogo size={14} weight="fill" />
                            WA
                          </a>
                          <button
                            type="button"
                            onClick={() => openEdit(lead)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded bg-[#F0F0F0] hover:bg-[#1B2D5E] hover:text-white transition-colors"
                          >
                            <PencilSimple size={14} />
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => openAddReview(lead)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded bg-[#FFF8E1] text-[#E8651A] hover:bg-[#E8651A] hover:text-white transition-colors"
                            title="Add Review"
                          >
                            <Star size={14} weight="fill" />
                            Add Review
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!editLead} onOpenChange={(open) => !open && setEditLead(null)}>
        <DialogContent className="font-body max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-[#1B2D5E]">Edit Inquiry</DialogTitle>
          </DialogHeader>
          {editLead && (
            <div className="space-y-4 pt-2">
              <p className="text-sm text-[#666]">
                {editLead.from_city} → {editLead.to_city} · {editLead.phone}
              </p>
              <div>
                <label className="text-xs font-medium text-[#888] mb-1 block">Status</label>
                <Select value={editStatus} onValueChange={setEditStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#888] mb-1 block">Notes</label>
                <Textarea
                  placeholder="Add notes…"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={3}
                />
              </div>
              <Button
                onClick={saveEdit}
                disabled={saving}
                className="w-full bg-[#1B2D5E] hover:bg-[#E8651A]"
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <TestimonialModal
        open={reviewModalOpen}
        onOpenChange={(open) => {
          setReviewModalOpen(open);
          if (!open) setReviewLead(null);
        }}
        initialValues={reviewInitialValues}
        onSaved={() => setReviewLead(null)}
      />
    </div>
  );
};

export default Leads;
